import { computed, reactive, watch } from 'vue';
import type { ComputedRef } from 'vue';
import type { CanvasEx } from '@/typings/canvas';
import type { ElementMapOption } from '@/components/DesignStudio/types';
import type { ShadowPatch, ShadowState } from '@/utils/shadowUtils';
import { NO_SHADOW, exportFormatSupportsShadow, nextShadowState } from '@/utils/shadowUtils';
import { ShadowTypeEnum } from '@/utils/enums/shadowType.enum';
import { useShadowColorOptions } from '@/composables/shadowColorOptions';
import type { ShadowColorElement, ShadowColorModel, ShadowColorOptionsApi } from '@/composables/shadowColorOptions';

/**
 * Everything an element has to expose to get a shadow panel. A new element type (a vector, a
 *  curved text) becomes shadow capable by implementing this on its fabric class, not by writing
 *  another panel: see ShadowSettings.vue and the two thin wrappers around it.
 */
export interface ShadowElement extends ShadowColorElement, Partial<ShadowState> {
    locked?: boolean;
    /**
     * Applies a whole patch in one pass. Elements are free to store the shadow however they need
     *  (a fabric filter, plain properties) as long as one call means one repaint: the panel never
     *  writes their properties one by one.
     */
    applyShadow(patch: ShadowPatch): void;
}

export interface ShadowModel extends ShadowState, ShadowColorModel {}

export interface UseShadowSettingsOptions<T extends ShadowElement> {
    /** Getter, not the value: the selected element changes while the panel stays mounted. */
    element: () => T | undefined;
    canvas: () => CanvasEx;
    editMode: () => boolean | undefined;
    exportFormat: () => string | undefined;
    /** Why *this* kind of element can't cast a shadow right now (a textured text, a traced image). */
    supportsShadow?: (element: T) => boolean;
    /** Opens the library picker, which lives in the AngularJS controller of each step. */
    openLibrarySelector: () => void;
    /** Step 1 locks the vector export formats while any element casts a shadow. */
    onShadowChange?: () => void;
}

export interface ShadowSettingsApi {
    model: ShadowModel;
    disabled: ComputedRef<boolean>;
    changeShadowType: (type: ShadowTypeEnum) => void;
    changeShadowOffset: (value: number) => void;
    changeShadowAngle: (value: number) => void;
    changeShadowBlur: (value: number) => void;
    colors: ShadowColorOptionsApi;
}

/**
 * State of a shadow panel: keeps a local model in sync with the selected element, decides when the
 *  panel is disabled and writes every change back through the element's applyShadow().
 *
 * All of the shadow rules live here so text, images and whatever comes next can't drift apart.
 */
export function useShadowSettings<T extends ShadowElement>(options: UseShadowSettingsOptions<T>): ShadowSettingsApi {
    const model = reactive<ShadowModel>({
        ...NO_SHADOW,
        shadowColorOption: 1,
        shadowColorLibraryId: undefined,
        shadowColorsMap: new Map<number, ElementMapOption>()
    });

    const colors = useShadowColorOptions<T>({
        element: options.element,
        canvas: options.canvas,
        editMode: options.editMode,
        model,
        openLibrarySelector: options.openLibrarySelector
    });

    const disabled = computed(() => {
        const element = options.element();
        if (!element || element.locked) return true;
        if (options.supportsShadow && !options.supportsShadow(element)) return true;

        return !exportFormatSupportsShadow(options.exportFormat());
    });

    watch(
        options.element,
        (element) => {
            // Selecting an element that has no shadow support (or nothing at all) has to clear the
            //  panel: keeping the previous element's values around showed a shadow that wasn't there.
            model.shadowType = element?.shadowType ?? null;
            model.shadowOffset = element?.shadowOffset || 0;
            model.shadowAngle = element?.shadowAngle || 0;
            model.shadowBlur = element?.shadowBlur || 0;
            model.shadowColorOption = element?.shadowColorOption || 1;
            model.shadowColorLibraryId = element?.shadowColorLibraryId;
            model.shadowColorsMap = new Map(element?.shadowColorsMap);
        },
        { immediate: true }
    );

    const write = (patch: ShadowPatch, ...editedProps: string[]) => {
        const element = options.element();
        if (!element) return;

        element.applyShadow(patch);
        if (options.editMode()) {
            element.setEditedProp(...editedProps);
        }
        options.canvas().requestRenderAll();
    };

    const changeShadowType = (type: ShadowTypeEnum) => {
        if (!options.element()) return;

        const next = nextShadowState(model, type);
        Object.assign(model, next);

        const patch: ShadowPatch = { ...next };
        // Turning the shadow on has to paint it with the selected color option instead of leaving
        //  the element on its fallback fill. It goes in the same patch so an element that rebuilds
        //  a filter out of it does so once, already complete.
        if (next.shadowType != null && !options.element()?.shadowColorLibraryId) {
            // Left out of the patch when there is no color option yet, so the element keeps the
            //  fill it already had instead of falling back to the default one.
            const fill = colors.resolveColorCss(model.shadowColorOption);
            if (fill) {
                patch.shadowFill = fill;
            }
        }

        write(patch, 'shadowType', 'shadowOffset', 'shadowAngle', 'shadowBlur');
        options.onShadowChange?.();
    };

    const changeShadowOffset = (value: number) => {
        model.shadowOffset = value;
        write({ shadowOffset: value }, 'shadowOffset');
    };

    const changeShadowAngle = (value: number) => {
        model.shadowAngle = value;
        write({ shadowAngle: value }, 'shadowAngle');
    };

    const changeShadowBlur = (value: number) => {
        model.shadowBlur = value;
        write({ shadowBlur: value }, 'shadowBlur');
    };

    return {
        model,
        disabled,
        changeShadowType,
        changeShadowOffset,
        changeShadowAngle,
        changeShadowBlur,
        colors
    };
}
